import React, { useMemo, useState } from "react";
import { Popover, Button } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import SearchBar from "./SearchBar";
import ScrollableWrapper from "./ScrollableWrapper";
import DateRangeCalendar, {
  formatDateRangeLabel,
} from "./DateRangeCalendar";
import { CustomCheckBox } from "../dashboard/ChartKeyPicker";

/**
 * Filters that live *in a table header cell* rather than in a toolbar above the
 * table (`prescriptionTab/FilterSelect` and `labBuilder/DateFilterSelect` are
 * the toolbar versions).
 *
 * The trigger is the column label plus a chevron and a count badge — no border,
 * no background, so the header still reads as a header. Both variants share
 * that trigger, so a date column and a value column are visually identical
 * until opened.
 *
 * Popover bodies are composed from existing pieces rather than forked:
 * `SearchBar` + `CustomCheckBox` for the value list, and the shared
 * `DateRangeCalendar` for the range picker.
 */

export interface ColumnFilterOption {
  key: string;
  label: string;
  /** Initials avatar beside the label — the design shows these for clients. */
  initials?: string;
}

/** The label + chevron + count trigger, shared by both variants. */
const FilterTrigger: React.FC<{
  title: string;
  open: boolean;
  count?: number;
  valueLabel?: string | null;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}> = ({ title, open, count = 0, valueLabel, onClick }) => {
  const theme: Theme = useTheme();
  const active = open || count > 0 || Boolean(valueLabel);

  return (
    <button type="button" style={styles.trigger(theme, active)} onClick={onClick}>
      <span style={styles.triggerTitle}>{title}</span>
      {count > 0 && <span style={styles.countBadge(theme)}>{count}</span>}
      {valueLabel && <span style={styles.valueLabel(theme)}>{valueLabel}</span>}
      <KeyboardArrowDownIcon style={styles.chevron(open)} />
    </button>
  );
};

interface ColumnFilterMenuProps {
  title: string;
  options: ColumnFilterOption[];
  selectedKeys: string[];
  onSelectionChange: (selected: string[]) => void;
  /** Adds a search field above the list, for long option lists (clients). */
  searchable?: boolean;
}

const ColumnFilterMenu: React.FC<ColumnFilterMenuProps> = ({
  title,
  options,
  selectedKeys,
  onSelectionChange,
  searchable = false,
}) => {
  const theme: Theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");

  const open = Boolean(anchorEl);

  const visibleOptions = useMemo(() => {
    if (!searchable || !search.trim()) return options;
    const needle = search.trim().toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(needle)
    );
  }, [options, search, searchable]);

  const toggle = (key: string) => {
    onSelectionChange(
      selectedKeys.includes(key)
        ? selectedKeys.filter((selected) => selected !== key)
        : [...selectedKeys, key]
    );
  };

  return (
    <>
      <FilterTrigger
        title={title}
        open={open}
        count={selectedKeys.length}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: { marginTop: "8px", boxShadow: "var(--box-shadow-general)" },
        }}
      >
        <div style={styles.panel(theme)}>
          {searchable && (
            <div style={styles.searchWrapper}>
              <SearchBar
                searchFilter={search}
                setSearchFilter={setSearch}
                placeholder="Search"
                customHeight="34px"
                fontSize="14px"
              />
            </div>
          )}
          <div style={styles.panelHeader(theme)}>
            <div style={styles.panelTitle(theme)}>Filters</div>
            <Button
              sx={styles.clearButton(theme)}
              onClick={() => onSelectionChange([])}
            >
              Clear
            </Button>
          </div>
          <ScrollableWrapper>
            {visibleOptions.length === 0 ? (
              <div style={styles.noOptions(theme)}>No matches</div>
            ) : (
              visibleOptions.map((option) => (
                <div
                  key={option.key}
                  style={styles.optionRow}
                  onClick={() => toggle(option.key)}
                >
                  <CustomCheckBox
                    selected={selectedKeys.includes(option.key)}
                  />
                  <div style={styles.optionLabel(theme)}>{option.label}</div>
                  {option.initials && (
                    <div style={styles.avatar(theme)}>{option.initials}</div>
                  )}
                </div>
              ))
            )}
          </ScrollableWrapper>
        </div>
      </Popover>
    </>
  );
};

export default ColumnFilterMenu;

interface ColumnDateFilterMenuProps {
  title: string;
  startDate: Date | null;
  endDate: Date | null;
  onDateChange: (start: Date | null, end: Date | null) => void;
}

/** The date-range variant: same trigger, the shared range calendar behind it. */
export const ColumnDateFilterMenu: React.FC<ColumnDateFilterMenuProps> = ({
  title,
  startDate,
  endDate,
  onDateChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [navDate, setNavDate] = useState<Date | null>(null);

  return (
    <>
      <FilterTrigger
        title={title}
        open={Boolean(anchorEl)}
        valueLabel={formatDateRangeLabel(startDate, endDate)}
        onClick={(event) => {
          event.stopPropagation();
          // Seed the cursor so the calendar opens on the selected month rather
          // than a stale one from a previous open.
          setNavDate(startDate ?? new Date());
          setAnchorEl(event.currentTarget);
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setNavDate(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: { marginTop: "8px", boxShadow: "var(--box-shadow-general)" },
        }}
      >
        <DateRangeCalendar
          title={title}
          startDate={startDate}
          endDate={endDate}
          onDateChange={onDateChange}
          navDate={navDate}
          setNavDate={setNavDate}
        />
      </Popover>
    </>
  );
};

const styles = {
  trigger: (theme: Theme, active: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: "4px",
    // A date filter's range label can be longer than its column, so the
    // trigger clips inside the header cell instead of running into the next
    // column's label.
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden" as "hidden",
    padding: 0,
    margin: 0,
    border: "none",
    background: "none",
    cursor: "pointer",
    outline: "none",
    WebkitTapHighlightColor: "transparent",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    textAlign: "left" as "left",
    color: active
      ? theme.colors_ds.PRIMARY_TEXT_MAIN
      : theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  triggerTitle: {
    flexShrink: 0,
  },
  countBadge: (theme: Theme) => ({
    minWidth: "18px",
    height: "18px",
    padding: "0 4px",
    borderRadius: "9px",
    backgroundColor: theme.colors_ds.PRIMARY_ICON_MAIN,
    color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    fontFamily: "var(--font-Bold)",
    fontSize: "11px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
  valueLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    minWidth: 0,
    whiteSpace: "nowrap" as "nowrap",
    overflow: "hidden" as "hidden",
    textOverflow: "ellipsis" as "ellipsis",
  }),
  chevron: (open: boolean) => ({
    fontSize: "16px",
    flexShrink: 0,
    transform: open ? "rotate(180deg)" : "rotate(0deg)",
    transition: "transform 0.3s ease",
  }),
  panel: (theme: Theme) => ({
    width: "300px",
    maxHeight: "400px",
    display: "flex",
    flexDirection: "column" as "column",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    padding: "16px",
    borderRadius: "8px",
  }),
  searchWrapper: {
    marginBottom: "16px",
    flexShrink: 0,
  },
  panelHeader: (theme: Theme) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: "8px",
    marginBottom: "8px",
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    flexShrink: 0,
  }),
  panelTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  clearButton: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    textTransform: "none" as "none",
    padding: "0",
    minWidth: 0,
    "&:hover": { backgroundColor: "transparent" },
  }),
  optionRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 4px",
    borderRadius: "4px",
    cursor: "pointer",
  },
  optionLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    flex: 1,
    whiteSpace: "normal" as "normal",
    wordBreak: "break-word" as "break-word",
  }),
  avatar: (theme: Theme) => ({
    width: "22px",
    height: "22px",
    flexShrink: 0,
    borderRadius: "50%",
    backgroundColor: theme.colorTokens.P[20],
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    fontFamily: "var(--font-Bold)",
    fontSize: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
  noOptions: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    padding: "8px 4px",
  }),
};
